import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "src/redux/reducers";
import { SearchResultsService } from "src/services";
import { SelectOption } from "src/types";
import useGetGlobalTasksSearchResults from "src/hooks/useGetGlobalTasksSearchResults";
import { generateSelectOptions } from "../utils";

interface UseGlobalCreatorDropdownDataProps {
  fieldValue: string[] | null | undefined;
  handleUpdateValue: (values: string[]) => void;
}

export default function useGlobalCreatorDropdownData({
  fieldValue,
  handleUpdateValue,
}: UseGlobalCreatorDropdownDataProps) {
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const [
    globalTasksSearchResultsState,
    initializeGlobalTasksSearchResultsFetch,
  ] = useGetGlobalTasksSearchResults();

  const isLoading = globalTasksSearchResultsState.type === "pending";

  let options: SelectOption[] = [];
  if (globalTasksSearchResultsState.type === "done") {
    options = generateSelectOptions(globalTasksSearchResultsState.data);
  }

  const handleUnselectOption = (option: SelectOption) => {
    setSelectedOptions((prev) => {
      const updatedOptions = prev.filter((el) => el.value !== option.value);
      const updatedOptionsValues = updatedOptions.map(
        (updatedOption) => updatedOption.value,
      );
      handleUpdateValue(updatedOptionsValues);

      return updatedOptions;
    });
  };

  const handleSelectOption = (option: SelectOption) => {
    if (selectedOptions.find((el) => el.value === option.value)) {
      handleUnselectOption(option);
    } else {
      setSelectedOptions((prev) => {
        const newOptions = [...prev, option];
        const selectedOptionsValues = newOptions.map(
          (selectedOption) => selectedOption.value,
        );
        handleUpdateValue(selectedOptionsValues);

        return newOptions;
      });
    }
  };

  const handleUserQuery = (query: string) => {
    if (query.length === 0) {
      initializeGlobalTasksSearchResultsFetch(activeWorkspaceUuid);
    } else {
      initializeGlobalTasksSearchResultsFetch(activeWorkspaceUuid, [query]);
    }
  };

  useEffect(() => {
    const getSelectedOptions = async (currentValues: string[]) => {
      const response = await SearchResultsService.getGlobalTasks(
        activeWorkspaceUuid,
        currentValues,
      );
      setSelectedOptions(generateSelectOptions(response));
    };

    if (fieldValue && fieldValue.length > 0) {
      getSelectedOptions(fieldValue);
    } else {
      setSelectedOptions([]);
    }
  }, [fieldValue, activeWorkspaceUuid]);

  return [
    handleSelectOption,
    handleUnselectOption,
    handleUserQuery,
    selectedOptions,
    options,
    isLoading,
  ] as const;
}
